import { flatMap, compact, compose, map, xor } from 'lodash/fp';

import { mapErrorMessages } from '../utils/challengeUtils';
import createReducer from '../utils/createReducer';
import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  RESET_CHALLENGE,
  RESET_MY_TEAMS,
  SET_SELECTED_LINEUP_IDS,
  SET_LINEUP_TYPE_FILTER,
  SUBMIT_MULTIPLE_LINEUP_ENTRY_REQUEST,
  SUBMIT_MULTIPLE_LINEUP_ENTRY_SUCCESS,
  SUBMIT_MULTIPLE_LINEUP_ENTRY_FAILURE,
  SWITCH_LINEUP_TYPE_FILTER
} from '../constants/actionTypes';
import {
  DELETE_LINEUPS,
  MY_LINEUP,
  MY_LINEUPS,
  MY_LINEUP_CHALLENGES,
  MY_SLATE_LINEUPS,
  TOGGLE_LINEUP_FAVORITE,
  CLEAR_FAVORITES
} from '../constants/apiTypes';
import decodeLineup from '../normalizers/decodeLineup';
import decodeChallenge from '../normalizers/decodeChallenge';

const initialState = {
  favoritesCleared: false,
  isClearing: false,
  isDeleting: false,
  isLoading: false,
  isLineupChallengesLoading: false,
  isSlateLineupsLoading: false,
  isTogglingFavorite: false,
  lineups: [],
  lineupChallenges: [],
  lineup: {},
  lineupsDeleted: false,
  lineupTypeFilters: [],
  selectedLineupIds: [],
  isEnteringChallenge: false,
  successfulEntryIds: [],
  submissionComplete: false,
  errorMessages: []
};

const myTeamsReducer = createReducer(initialState, {
  [API_CALL_REQUEST]: (state, action) => {
    switch (action.api) {
      case CLEAR_FAVORITES:
        return {
          isClearing: true,
          favoritesCleared: false
        };
      case DELETE_LINEUPS:
        return {
          isDeleting: true,
          lineupsDeleted: false
        };
      case MY_LINEUP:
      case MY_LINEUPS:
        return {
          isLoading: true
        };
      case MY_LINEUP_CHALLENGES:
        return {
          isLineupChallengesLoading: true
        };
      case MY_SLATE_LINEUPS:
        return {
          isSlateLineupsLoading: true
        };
      case TOGGLE_LINEUP_FAVORITE:
        return {
          isTogglingFavorite: true
        };
      default:
        return null;
    }
  },

  [API_CALL_SUCCESS]: (state, action) => {
    switch (action.api) {
      case CLEAR_FAVORITES:
        return {
          isClearing: false,
          favoritesCleared: true
        };
      case DELETE_LINEUPS:
        return {
          isDeleting: false,
          lineupsDeleted: true
        };
      case MY_LINEUPS:
      case MY_SLATE_LINEUPS:
        return {
          isLoading: false,
          isSlateLineupsLoading: false,
          lineups: (action.data || []).map(lineup => decodeLineup(lineup, action.meta))
        };
      case MY_LINEUP:
        return {
          isLoading: false,
          lineup: decodeLineup(action.data, action.meta)
        };
      case MY_LINEUP_CHALLENGES:
        return {
          isLineupChallengesLoading: false,
          lineupChallenges: (action.data || []).map(ch => decodeChallenge(ch, action.meta))
        };
      case TOGGLE_LINEUP_FAVORITE:
        return {
          isTogglingFavorite: false,
          lineups: state.lineups.map(lineup =>
            lineup.id === action.id ? { ...lineup, favorite: action.favorite } : { ...lineup }
          )
        };
      default:
        return null;
    }
  },

  [API_CALL_FAILURE]: (state, action) => {
    switch (action.api) {
      case CLEAR_FAVORITES:
        return {
          isClearing: false,
          favoritesCleared: false
        };
      case DELETE_LINEUPS:
        return {
          isDeleting: false,
          lineupsDeleted: false
        };
      case MY_LINEUP:
      case MY_LINEUPS:
      case MY_LINEUP_CHALLENGES:
      case MY_SLATE_LINEUPS:
        return {
          isLoading: false,
          isLineupChallengesLoading: false,
          isSlateLineupsLoading: false
        };
      case TOGGLE_LINEUP_FAVORITE:
        return {
          isTogglingFavorite: false
        };
      default:
        return null;
    }
  },
  [RESET_MY_TEAMS]: () => initialState,
  [RESET_CHALLENGE]: () => initialState,
  [SET_LINEUP_TYPE_FILTER]: (state, action) => ({
    lineupTypeFilters: xor(state.lineupTypeFilters, [action.value])
  }),
  [SWITCH_LINEUP_TYPE_FILTER]: (state, action) => {
    if (state.lineupTypeFilters[0] === action.value)
      return {
        lineupTypeFilters: []
      };
    else
      return {
        lineupTypeFilters: [action.value]
      };
  },
  [SET_SELECTED_LINEUP_IDS]: (state, { lineupIds }) => ({
    selectedLineupIds: lineupIds
  }),
  [SUBMIT_MULTIPLE_LINEUP_ENTRY_REQUEST]: () => ({
    isEnteringChallenge: true,
    successfulEntryIds: [],
    submissionComplete: false
  }),
  [SUBMIT_MULTIPLE_LINEUP_ENTRY_SUCCESS]: (state, action) => ({
    isEnteringChallenge: false,
    successfulEntryIds: flatMap(res => map('id', res.data))(action.payload),
    submissionComplete: true,
    errorMessages:
      compose(
        compact,
        flatMap(data => mapErrorMessages(data.errors))
      )(action.payload) || []
  }),
  [SUBMIT_MULTIPLE_LINEUP_ENTRY_FAILURE]: (state, action) => ({
    isEnteringChallenge: false,
    submissionComplete: true,
    errorMessages: mapErrorMessages(action.payload.errors)
  })
});

export default myTeamsReducer;
